import type { z } from "zod";
import { ERROR_CODES, type ErrorCode, type ApiResponse } from "@nh/shared";

/**
 * Error tipado del API. Todo fallo — de red, de contrato o de negocio —
 * llega al llamador como un `ApiError` con un `ErrorCode` de `@nh/shared`,
 * nunca como un string suelto. El frontend ramifica sobre `code`, jamás
 * sobre el texto de `message`.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string[]> | undefined;
  readonly requestId: string | undefined;
  /** Detalle de por qué falló la validación del esquema, si aplica. */
  override readonly cause: unknown;

  constructor(params: {
    code: ErrorCode;
    message: string;
    status?: number;
    fields?: Record<string, string[]> | undefined;
    requestId?: string | undefined;
    cause?: unknown;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.code = params.code;
    this.status = params.status ?? 0;
    this.fields = params.fields;
    this.requestId = params.requestId;
    this.cause = params.cause;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** Cuerpo que se serializa como JSON. Usa `rawBody` si necesitas otra cosa. */
  body?: unknown;
  rawBody?: BodyInit | null;
  /** Base del API. En dev el proxy de Vite manda /api → localhost:4000. */
  baseUrl?: string;
}

const DEFAULT_BASE_URL = "/api";

/**
 * Hace una petición al API y devuelve `data` YA validado contra el esquema
 * de datos del endpoint.
 *
 * La validación es obligatoria a propósito: si el backend cambia la forma de
 * una respuesta, esto revienta aquí, en el instante, con `INTERNAL` y el
 * detalle de Zod — en vez de propagar un `undefined` hasta la UI.
 *
 * @param path   ruta relativa al API, p. ej. "/health"
 * @param schema esquema de la RESPUESTA COMPLETA (envelope), típicamente
 *               `responseSchema(algoSchema)` de `@nh/shared`
 */
export async function apiFetch<TSchema extends z.ZodType<ApiResponse<unknown>>>(
  path: string,
  schema: TSchema,
  options: ApiFetchOptions = {},
): Promise<Extract<z.infer<TSchema>, { ok: true }>["data"]> {
  const { body, rawBody, baseUrl = DEFAULT_BASE_URL, headers, ...rest } = options;

  const init: RequestInit = {
    ...rest,
    credentials: "include",
    /*
     * El API nunca se lee del caché HTTP (bug real de producción): una
     * respuesta HTML cacheada en la ventana sin rewrite dejó a la
     * tienda leyendo "<!doctype" para siempre. Un API es datos vivos.
     */
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  } else if (rawBody !== undefined) {
    init.body = rawBody;
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, init);
  } catch (cause) {
    // El backend no responde (apagado, DNS, CORS, offline).
    throw new ApiError({
      code: ERROR_CODES.INTERNAL,
      message: "No se pudo contactar al servidor.",
      cause,
    });
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    throw new ApiError({
      code: ERROR_CODES.INTERNAL,
      message: "El servidor respondió algo que no es JSON.",
      status: response.status,
      cause,
    });
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // Desajuste de contrato: el cuerpo no tiene la forma acordada.
    throw new ApiError({
      code: ERROR_CODES.INTERNAL,
      message: "La respuesta del servidor no cumple el contrato del API.",
      status: response.status,
      cause: parsed.error,
    });
  }

  const envelope = parsed.data as ApiResponse<unknown>;

  if (!envelope.ok) {
    throw new ApiError({
      code: toErrorCode(envelope.error.code),
      message: envelope.error.message,
      status: response.status,
      fields: envelope.error.fields,
      requestId: envelope.error.requestId,
    });
  }

  return envelope.data as Extract<z.infer<TSchema>, { ok: true }>["data"];
}

const KNOWN_CODES = new Set<string>(Object.values(ERROR_CODES));

/** Un código desconocido no debe romper el ramificado: cae a INTERNAL. */
function toErrorCode(code: string): ErrorCode {
  return KNOWN_CODES.has(code) ? (code as ErrorCode) : ERROR_CODES.INTERNAL;
}

/** Type guard para usar en catch / error boundaries. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
